import { useState, useCallback } from 'react';
import { Dialog, DialogContent } from '@ui/dialog.tsx';
import { RenewalProgramIntroStep } from './steps/renewal-program-intro-step.tsx';
import { RenewalProgramOverviewStep } from './steps/renewal-program-overview-step.tsx';
import { RenewalProgramFormStep } from './steps/renewal-program-form-step.tsx';
import { RenewalProgramSuccessStep } from './steps/renewal-program-success-step.tsx';

type RenewalPhase = 'intro' | 'overview' | 'form' | 'success';

interface RenewalProgramDialogLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Layout principal pour le renouvellement du programme.
 * Gère les différentes phases : introduction, aperçu, et formulaire avec stepper.
 */
export const RenewalProgramDialogLayout = ({
  open,
  onOpenChange,
  onSuccess
}: RenewalProgramDialogLayoutProps) => {
  const [currentPhase, setCurrentPhase] = useState<RenewalPhase>('intro');

  const handleClose = useCallback(() => {
    setCurrentPhase('intro');
    onOpenChange(false);
  }, [onOpenChange]);

  const handleIntroNext = useCallback(() => {
    setCurrentPhase('overview');
  }, []);

  const handleOverviewNext = useCallback(() => {
    setCurrentPhase('form');
  }, []);

  const handleOverviewLater = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleFormFinish = useCallback(() => {
    onSuccess?.();
    setCurrentPhase('success');
  }, [onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-14 max-w-2xl max-h-[90vh] overflow-y-auto scrollable !rounded-3xl">
        {currentPhase === 'intro' && (
          <RenewalProgramIntroStep onNext={handleIntroNext} onClose={handleClose} />
        )}

        {currentPhase === 'overview' && (
          <RenewalProgramOverviewStep onStart={handleOverviewNext} onLater={handleOverviewLater} />
        )}

        {currentPhase === 'form' && (
          <RenewalProgramFormStep
            onFinish={handleFormFinish}
            onBack={() => setCurrentPhase('overview')}
          />
        )}

        {currentPhase === 'success' && <RenewalProgramSuccessStep onClose={handleClose} />}
      </DialogContent>
    </Dialog>
  );
};
